/** @format */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppBridge } from '@shopify/app-bridge-react';

/**
 * Route Transition Indicator
 * Uses Shopify's native loading API for consistent UX across Shopify admin
 * Shows loading indicator during route changes to prevent "frozen" feeling
 * Provides immediate visual feedback when navigation occurs
 * 
 * NOTE: This provides immediate feedback on navigation click.
 * Individual pages may keep the loading indicator active longer if they're
 * still fetching data (e.g., GraphQL queries in MyProducts, MyOrders, Settings)
 */
export default function RouteTransitionIndicator() {
    const location = useLocation();
    const [prevLocation, setPrevLocation] = useState(location.pathname);
    const shopify = useAppBridge();

    useEffect(() => {
        // Detect route change
        if (location.pathname !== prevLocation) {
            // Show Shopify's native loading indicator immediately
            shopify.loading(true);

            // Extended timeout to cover initial component mount and data fetching
            // This ensures users see loading feedback during the critical initial render
            // Individual pages will control the loading state after this timeout
            const timer = setTimeout(() => {
                // Don't force hide - let individual pages control loading state
                // Only hide if no page has taken over the loading state
                setPrevLocation(location.pathname);
            }, 1000); // Extended from 500ms to 1000ms for better coverage

            return () => {
                clearTimeout(timer);
                // Don't force hide on cleanup - let pages control their own loading state
            };
        }
    }, [location.pathname, prevLocation, shopify]);

    // This component doesn't render anything - it just controls Shopify's loading state
    return null;
}

